// Facturation de la plateforme à ses clients (super-admin) : tarifs des modules, abonnement de chaque client, remises et offres.
// Serveur : functions/api/plateforme-abonnements.js. Cadrage : docs/CADRAGE-FACTURATION-PLATEFORME.md.
import { apiFetch } from './api';

export type ModeTarif = 'gratuit' | 'fixe' | 'paliers' | 'degressif';
export interface PalierTarif { quantite: number; prix_eur: number }
/** Une tranche d'un tarif dégressif : prix par unité jusqu'à `jusqu_a` (null = sans plafond). */
export interface TrancheTarif { jusqu_a: number | null; prix_unite: number }

export interface ModuleTarif {
    name: string;
    label: string;
    description: string;
    statut: 'disponible' | 'bientot';
    core: boolean;
    /** Types de compte auxquels le module s'adresse : prestation, restauration, hotellerie. */
    types?: string[];
    /** Le bloc de modules vendus ensemble (un seul prix, sur le pilote) auquel appartient ce module. `compris` : son prix est celui du bloc. */
    bloc?: { name: string; label: string; pilote: string; compris: boolean; membres: { name: string; label: string }[] } | null;
    /** Ce que le module exige pour fonctionner (activé en même temps). `core` : module de base, toujours présent. */
    necessite?: { name: string; label: string; core: boolean; statut: 'disponible' | 'bientot' }[];
    /** Ce qui va bien avec lui sans être obligatoire. */
    bon_avec?: { name: string; label: string; types?: string[] }[];
    mode: ModeTarif;
    prix_eur: number;
    paliers: PalierTarif[];
    tranches?: TrancheTarif[];
    /** Vrai pour le module des salariés : le mode « dégressif » (prix par salarié qui baisse à chaque tranche) lui est ouvert. */
    degressif_possible?: boolean;
    /** Conseillère IA seulement : prix d'une conversation au-delà des conversations incluses (0 = non proposé). */
    prix_depassement_eur?: number;
}
/** Un bloc de modules vendus ensemble à un seul prix (créé par le super-admin). */
export interface BlocDef { name: string; label: string; pilote: string; membres: string[] }
export interface FamilleTarifs { famille: string; modules: ModuleTarif[] }

export type TypeRemise = 'pourcent' | 'montant' | 'mois_offerts' | 'offert';
export interface Remise {
    id: string;
    module_name: string;
    type: TypeRemise;
    valeur: number;
    mois_restants: number | null;
    motif: string;
    actif: boolean;
    created_at: string;
}

export interface LigneAbonnement {
    module_name: string;
    label: string;
    famille: string;
    mode: ModeTarif;
    prix_eur: number;
    palier: number | null;
    a_choisir: boolean;
    paliers: PalierTarif[];
    tranches?: TrancheTarif[];
    tarif_actuel_eur: number | null;
    ecart_tarif: boolean;
    debut: string;
    /** Ce module est compris dans la formule du client : il n'est pas facturé à part. */
    inclus_formule?: boolean;
    /** Conseillère IA comprise dans la formule : conversations gratuites par mois (seul le surplus d'un palier plus grand se facture). */
    conversations_incluses?: number;
    /** Consommation du mois quand elle se compte (avis rédigés). */
    utilise?: number | null;
}

export interface ApercuFacture {
    lignes: { module_name: string; label: string; prix_eur: number; remise_eur: number; net_eur: number; remises: { id: string; type: string; libelle: string; motif: string }[] }[];
    total_brut_eur: number;
    sous_total_eur: number;
    remises_globales: { id: string; type: string; libelle: string; motif: string; montant_eur: number }[];
    total_eur: number;
    economie_eur: number;
}

export interface VueAbonnementClient {
    client: { id: string; nom: string; type: string };
    familles: string[];
    lignes: LigneAbonnement[];
    remises: Remise[];
    apercu: ApercuFacture;
    /** Modules que ce client peut avoir mais n'a pas activés, avec leur tarif actuel. */
    disponibles: { module_name: string; label: string; famille: string; mode: ModeTarif; prix_eur: number; paliers: PalierTarif[] }[];
    modules_non_tarifes: number;
    /** Vrai quand la facturation de ce type de client est offerte (réglage global) : aucun prix, aucune limite. */
    facturation_offerte?: boolean;
    /** Verrou propre à ce client : il ne reçoit jamais de facture ni de limite. */
    client_offert?: boolean;
    /** Formule du client (prix figé), si elle en a une. */
    formule?: { id: string; nom: string; prix_eur: number; modules: string[]; inclus_detail: { label: string; detail: string }[]; modules_commutables: { module_name: string; label: string; a_part: boolean }[] } | null;
    /** Places de comptes collaborateurs : limite (null = aucune), places utilisées, demande en cours. */
    places: PlacesComptes;
    demandes_places: DemandePlaces[];
}

export interface PlacesComptes { limite: number | null; utilises: number; libres: number | null; prix_eur: number; paliers: PalierTarif[]; demande: { id: string; quantite: number; message: string; created_at: string } | null }
export interface DemandePlaces { module_name?: string; id: string; client_id: string; quantite: number; message: string; demandee_par: string; created_at: string; etablissement?: string }

export interface NouvelleRemise { module_name: string; type: TypeRemise; valeur?: number; mois?: number | null; motif?: string }

export const abonnementsApi = {
    tarifs: (): Promise<{ familles: FamilleTarifs[]; blocs: BlocDef[] }> => apiFetch('/admin/abonnements/tarifs'),
    modulesBase: (): Promise<{ modules: string[] }> => apiFetch('/admin/abonnements/modules-base'),
    saveModulesBase: (modules: string[]): Promise<{ ok: boolean; modules: string[]; clients_touches: number }> => apiFetch('/admin/abonnements/modules-base', { method: 'PUT', body: JSON.stringify({ modules }) }),
    creerBloc: (b: { label: string; pilote: string; membres: string[] }) => apiFetch('/admin/abonnements/blocs', { method: 'POST', body: JSON.stringify(b) }),
    modifierBloc: (name: string, b: { label: string; pilote: string; membres: string[] }) => apiFetch(`/admin/abonnements/blocs/${name}`, { method: 'PUT', body: JSON.stringify(b) }),
    supprimerBloc: (name: string) => apiFetch(`/admin/abonnements/blocs/${name}`, { method: 'DELETE' }),
    saveTarif: (module: string, payload: { mode: ModeTarif; prix_eur?: number; paliers?: PalierTarif[]; tranches?: TrancheTarif[]; prix_depassement_eur?: number }) =>
        apiFetch(`/admin/abonnements/tarifs/${module}`, { method: 'PUT', body: JSON.stringify(payload) }),
    client: (clientId: string): Promise<VueAbonnementClient> => apiFetch(`/admin/abonnements/clients/${clientId}`),
    /** Fait payer un module de la formule à plein tarif à ce client (a_part = true), ou le remet dans la formule. */
    moduleAPart: (clientId: string, module: string, aPart: boolean): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/formule/modules/${module}`, { method: 'PUT', body: JSON.stringify({ a_part: aPart }) }),
    choisirPalier: (clientId: string, module: string, palier: number): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/modules/${module}`, { method: 'PUT', body: JSON.stringify({ palier }) }),
    /** Retire la limite de comptes d'un client (il n'est plus facturé pour ses comptes). */
    retirerLimite: (clientId: string, module: string): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/modules/${module}`, { method: 'PUT', body: JSON.stringify({ palier: null }) }),
    demandesPlaces: (): Promise<{ demandes: DemandePlaces[] }> => apiFetch('/admin/abonnements/demandes-places'),
    traiterDemandePlaces: (clientId: string, id: string, decision: 'accepter' | 'refuser', payload: { quantite?: number; motif?: string } = {}): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/demandes-places/${id}/${decision}`, { method: 'POST', body: JSON.stringify(payload) }),
    aligner: (clientId: string, module: string): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/modules/${module}`, { method: 'PUT', body: JSON.stringify({ aligner: true }) }),
    ajouterRemise: (clientId: string, payload: NouvelleRemise): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/remises`, { method: 'POST', body: JSON.stringify(payload) }),
    retirerRemise: (clientId: string, id: string): Promise<VueAbonnementClient> =>
        apiFetch(`/admin/abonnements/clients/${clientId}/remises/${id}`, { method: 'DELETE' }),
};

/** Ce que veut dire la quantité d'un palier, par module (le reste du catalogue n'a pas de paliers pour l'instant). */
/** Le tarif d'un module tel qu'un client le voit : mode, prix mensuel et paliers. */
export interface TarifModule { mode: ModeTarif; prix_eur: number; paliers: PalierTarif[]; tranches?: TrancheTarif[] }
/** Prix mensuel affiché : le prix fixe, ou le plus petit palier pour un module à paliers ; 0 si gratuit. */
export const prixMensuelModule = (t?: TarifModule | null): number =>
    !t ? 0 : t.mode === 'fixe' ? Number(t.prix_eur) || 0 : t.mode === 'paliers' && t.paliers.length ? Math.min(...t.paliers.map((p) => Number(p.prix_eur) || 0)) : 0;
export const estModulePayant = (t?: TarifModule | null): boolean => prixMensuelModule(t) > 0;
export const libelleTarifModule = (t?: TarifModule | null): string => {
    if (!estModulePayant(t)) return 'Gratuit';
    const prix = `${(Math.round(prixMensuelModule(t) * 100) / 100).toString().replace('.', ',')} €`;
    return `${t?.mode === 'paliers' ? 'dès ' : ''}${prix} par mois`;
};

export const UNITE_PALIER: Record<string, string> = { conseillere_ia: 'conversations par mois', avis_ia: 'avis rédigés par mois', comptes_collaborateurs: 'comptes collaborateurs', employes: 'salariés', planning: 'salariés', planning_rdv: 'salariés', portail_salarie: 'salariés', marketing_pub: 'e-mails de campagne par mois' };
/** Un seul élément de ces unités, pour « soit 0,38 € la conversation ». */
export const UNITE_SIMPLE: Record<string, string> = { conseillere_ia: 'la conversation', avis_ia: 'l’avis rédigé', employes: 'le salarié', planning: 'le salarié', planning_rdv: 'le salarié', portail_salarie: 'le salarié' };
export const uniteDe = (module: string) => UNITE_PALIER[module] || 'unités';
